import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { PermissionGroup } from 'src/app/constants/settings.enum';
import { PermissionModel } from 'src/app/models/api/permission.model';
import { LevelEnum } from 'src/app/constants/level.enum';
@Injectable({
    providedIn: 'root'
})
/**
 * INSTEAD use Permissions Service - It uses this to get Permissions but also HOLDS them in FE
 */
export class PermissionsApiClient {
    private API_ENDPOINT = environment.apiUrl + "/permissions";
    private _organisationPermissionsCache!: Observable<PermissionModel[]> | null;

    constructor(private httpClient: HttpClient) { }

    /**
         * Getting the permissions for organisations
     * @param id 
     * @param refreshCache Whether even WHEN theId is the same, the stored cache should be refreshed
     * @returns 
     */
    getOrganisationPermissions(id: string, refreshCache: boolean = false) {
        if (sessionStorage.getItem('organisationGuid') === id) {
            return this.getOrganisationPermissionsCache(id, refreshCache);
        } else {
            return this.getOrganisationPermissionsNonCache(id);
        }
    }
    getOrganisationPermissionsByGroupCache(organisationGuid: string, permissionGroup: PermissionGroup | null = null): Observable<PermissionModel[]> {
        if (permissionGroup === null) {
            return this.getOrganisationPermissions(organisationGuid);
        } else {
            return this.getOrganisationPermissions(organisationGuid).pipe(map(permission => permission.filter(perm => perm.configurationGroup === permissionGroup)));
        }
    }
    getOrganisationPermissionsCache(id: string, refreshCache: boolean = false) {
        if (!this._organisationPermissionsCache || refreshCache) {
            this._organisationPermissionsCache = this.getOrganisationPermissionsNonCache(id).pipe(
                map(data => data
                ),
                shareReplay(1)
            );
        }
        return this._organisationPermissionsCache;
    }
    getOrganisationPermissionsNonCache(id: string) {
        return this.httpClient.get<PermissionModel[]>(this.API_ENDPOINT + "/organisation/" + id + "/getPermissions");
    }

    /**
     * Getting the permissions for whitelabels
    */
    getWhitelabelPermissions(id: string) {
        return this.httpClient.get<PermissionModel[]>(this.API_ENDPOINT + "/whitelabel/" + id + "/getPermissions");
    }
    getWhitelabelPermissionsByGroup(whitelabelGuid: string, permissionGroup: PermissionGroup | null = null): Observable<PermissionModel[]> {
        if (permissionGroup === null) {
            return this.getWhitelabelPermissions(whitelabelGuid);
        } else {
            return this.getWhitelabelPermissions(whitelabelGuid).pipe(map(permission => permission.filter(perm => perm.configurationGroup === permissionGroup)));
        }
    }

    /**
     * Getting the permissions for system
    */
    getSystemPermissions() {
        return this.httpClient.get<PermissionModel[]>(this.API_ENDPOINT + "/system/getPermissions");
    }
    getSystemPermissionsByGroup(permissionGroup: PermissionGroup | null = null): Observable<PermissionModel[]> {
        if (permissionGroup === null) {
            return this.getSystemPermissions();
        } else {
            return this.getSystemPermissions().pipe(map(permission => permission.filter(perm => perm.configurationGroup === permissionGroup)));
        }
    }


    /**
     * Made it possible to update permissions values.
     * @param id - The id from the organisation/whitelabel
     * @param permissionName - Name of the permission to update
     * @param permissionValue - Value for the permission to update
     * @param level - This is a LevelEnum so it most be OrganisationLevel/WhitelabelLevel/SystemLevel
    */
    updatePermissionValue(id: string, permissionName: string, permissionValue: boolean, level: LevelEnum) {
        if (level === LevelEnum.OrganisationLevel) return this.updatePermissionValueForOrganisation(id, permissionName, permissionValue);
        if (level === LevelEnum.WhitelabelLevel) return this.updatePermissionValueForWhitelabel(id, permissionName, permissionValue);
        if (level === LevelEnum.SystemLevel) return this.updatePermissionValueForSystem(permissionName, permissionValue);
        return new Observable;
    }
    private updatePermissionValueForOrganisation(id: string, permissionName: string, permissionValue: boolean) {
        return this.httpClient.put(this.API_ENDPOINT + "/organisation/" + id + "/setPermissionValue", { Name: permissionName, Value: permissionValue });
    }
    private updatePermissionValueForWhitelabel(id: string, permissionName: string, permissionValue: boolean) {
        return this.httpClient.put(this.API_ENDPOINT + "/whitelabel/" + id + "/setPermissionValue", { Name: permissionName, Value: permissionValue });
    }
    private updatePermissionValueForSystem(permissionName: string, permissionValue: boolean) {
        return this.httpClient.put(this.API_ENDPOINT + "/system/setPermissionValue", { Name: permissionName, Value: permissionValue });
    }


    /**
     * Made it possible to update permissions configuration.
     * @param id - The id from the organisation/whitelabel
     * @param permissionName - Name of the permission to update
     * @param level - This is a LevelEnum so it most be OrganisationLevel/WhitelabelLevel/SystemLevel
     * @param permissionIsDisabled - When permission needs to be disabled this value is a boolean, else it would be null
     * @param permissionIsHidden - When permission needs to be hidden this value is a boolean, else it would be null
     * @param permissionIsLocked - When permission needs to be locked this value is a boolean, else it would be null
    */
    updatePermissionConfiguration(id: string, permissionName: string, level: LevelEnum, permissionIsDisabled: boolean | null = null, permissionIsHidden: boolean | null = null, permissionIsLocked: boolean | null = null) {
        if (level === LevelEnum.OrganisationLevel) return this.updatePermissionConfigurationForOrganisation(id, permissionName, permissionIsDisabled, permissionIsHidden, permissionIsLocked);
        if (level === LevelEnum.WhitelabelLevel) return this.updatePermissionConfigurationForWhitelabel(id, permissionName, permissionIsDisabled, permissionIsHidden, permissionIsLocked);
        if (level === LevelEnum.SystemLevel) return this.updatePermissionConfigurationForSystem(permissionName, permissionIsDisabled, permissionIsHidden, permissionIsLocked);
        return new Observable;

    }
    private updatePermissionConfigurationForOrganisation(id: string, permissionName: string, permissionIsDisabled: boolean | null = null, permissionIsHidden: boolean | null = null, permissionIsLocked: boolean | null = null) {
        return this.httpClient.put(this.API_ENDPOINT + "/organisation/" + id + "/setPermissionConfiguration", { Name: permissionName, IsDisabled: permissionIsDisabled, IsHidden: permissionIsHidden, IsLocked: permissionIsLocked });
    }
    private updatePermissionConfigurationForWhitelabel(id: string, permissionName: string, permissionIsDisabled: boolean | null = null, permissionIsHidden: boolean | null = null, permissionIsLocked: boolean | null = null) {
        return this.httpClient.put(this.API_ENDPOINT + "/whitelabel/" + id + "/setPermissionConfiguration", { Name: permissionName, IsDisabled: permissionIsDisabled, IsHidden: permissionIsHidden, IsLocked: permissionIsLocked });
    }
    private updatePermissionConfigurationForSystem(permissionName: string, permissionIsDisabled: boolean | null = null, permissionIsHidden: boolean | null = null, permissionIsLocked: boolean | null = null) {
        return this.httpClient.put(this.API_ENDPOINT + "/system/setPermissionConfiguration", { Name: permissionName, IsDisabled: permissionIsDisabled, IsHidden: permissionIsHidden, IsLocked: permissionIsLocked });
    }

    /**
     * Made it possible to force permission value and configuration. The value in de db will be the right value. It will not been de same as in the FE
     * @param id - The id from the organisation/whitelabel
     * @param permissionName - Name of the permission to update
     * @param level - This is a LevelEnum so it most be WhitelabelLevel/SystemLevel
    */
    public forcePermissionValueAndConfiguration(id: string, permissionName: string, level: LevelEnum) {
        if (level === LevelEnum.WhitelabelLevel) return this.forcePermissionValueAndConfigurationForWhitelabel(id, permissionName);
        if (level === LevelEnum.SystemLevel) return this.forcePermissionValueAndConfigurationForSystem(permissionName);
        return new Observable;
    }
    private forcePermissionValueAndConfigurationForWhitelabel(id: string, permissionName: string) {
        return this.httpClient.put(this.API_ENDPOINT + "/whitelabel/" + id + "/forcePermission", { Name: permissionName });
    }
    private forcePermissionValueAndConfigurationForSystem(permissionName: string) {
        return this.httpClient.put(this.API_ENDPOINT + "/system/forcePermission", { Name: permissionName });
    }
}